import { useMemo } from 'react';

import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridPinnedColumnFields,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import { GeneratikTableColumn } from '../GeneratikTable.types';

export interface UseSystemColumnLayoutParams<R extends GridValidRowModel> {
  columns: GeneratikTableColumn<R>[];
  autoHiddenColumns: Set<string>;
  userPinnedColumns?: GridPinnedColumnFields;
  columnVisibilityModel?: Record<string, boolean>;
  groupByField?: string | null;
}

export interface UseSystemColumnLayoutReturn {
  effectivePinnedColumns: GridPinnedColumnFields;
  effectiveColumnVisibilityModel: Record<string, boolean>;
}

const useSystemColumnLayout = <R extends GridValidRowModel>({
  columns,
  autoHiddenColumns,
  userPinnedColumns,
  columnVisibilityModel,
  groupByField,
}: UseSystemColumnLayoutParams<R>): UseSystemColumnLayoutReturn => {
  const effectivePinnedColumns = useMemo(() => {
    const hasActions =
      columns.some((col) => col.field === 'actions') && !autoHiddenColumns.has('actions');
    const left = [
      GRID_CHECKBOX_SELECTION_FIELD,
      ...(autoHiddenColumns.has(GRID_TREE_DATA_GROUPING_FIELD)
        ? []
        : [GRID_TREE_DATA_GROUPING_FIELD]),
      ...(hasActions ? ['actions'] : []),
      ...(userPinnedColumns?.left?.filter(
        (f) =>
          f !== GRID_CHECKBOX_SELECTION_FIELD &&
          f !== GRID_TREE_DATA_GROUPING_FIELD &&
          f !== GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD &&
          f !== 'actions' &&
          !autoHiddenColumns.has(f),
      ) ?? []),
    ];

    return { left, right: userPinnedColumns?.right };
  }, [userPinnedColumns, columns, autoHiddenColumns]);

  const effectiveColumnVisibilityModel = useMemo(() => {
    const model = { ...columnVisibilityModel };
    if (groupByField) {
      const groupCol = columns.find((col) => col.field === groupByField);
      if (!groupCol || groupCol.hideable !== false) {
        model[groupByField] = false;
      }
    }
    autoHiddenColumns.forEach((field) => {
      model[field] = false;
    });
    // Force-show columns with isRowEmpty that are not currently auto-hidden.
    // Without this, the auto-hide state gets persisted and the column stays hidden
    // even after rows change (e.g. filtering to drafts after viewing published rows).
    columns.forEach((col) => {
      if (col.isRowEmpty && !autoHiddenColumns.has(col.field)) {
        model[col.field] = true;
      }
    });
    return model;
  }, [columnVisibilityModel, groupByField, autoHiddenColumns, columns]);

  return { effectivePinnedColumns, effectiveColumnVisibilityModel };
};

export default useSystemColumnLayout;
